<script setup>
import '@vuepic/vue-datepicker/dist/main.css'
import AppLayout from "@/Layouts/AppLayout.vue";
import {computed, ref, watch} from "vue";
import {FwbInput} from "flowbite-vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import {Link, useForm, usePage} from "@inertiajs/inertia-vue3";
import Datepicker from "@vuepic/vue-datepicker";
import InputLabel from "@/Components/InputLabel.vue";
import SelectInput from "@/Components/SelectInput.vue";
import InputError from "@/Components/InputError.vue";
import MediaUpload from "@/Components/Common/Media/MediaUpload.vue";
import DisabledInput from "@/Components/DisabledInput.vue";

const props = defineProps({
  backRoute: {
    type: String,
    default() {
      return 'admin.fill-orders.incoming';
    }
  },
  fillOrder: {
    type: Object,
    default() {
      return null
    }
  },
  vendors: Array,
  companies: Array,
  media: Object,
});
const fillOrderProperty = computed(() => usePage().props.value.fillOrder);
let fillOrder = fillOrderProperty.value;
watch(fillOrderProperty, (value) => {
  fillOrder = fillOrderProperty.value;
  resetForm(value);
  if (fillOrder?.oid) {
    pageTitle.value = 'Edit Fill Order';
  }
});
const pageTitle = ref('Add Fill Order');
if (fillOrder?.oid) {
  pageTitle.value = 'Edit Fill Order';
}

const saving = ref(false);
const datePlaced = ref(fillOrder?.placed);
const dateExpected = ref(fillOrder?.expected);
const form = useForm({
  _method: 'POST',
  vendor: fillOrder?.vendor || null,
  client: fillOrder?.client || null,
  prj_name: fillOrder?.prj_name || null,
  pu_order: fillOrder?.pu_order || null,
  placed: null,
  expected: null,
  trk_num: fillOrder?.trk_num || null
});

const saveForm = () => {
  let url = route('admin.fill-orders.store');
  if (props.fillOrder?.oid) {
    form._method = 'PUT';
    url = route('admin.fill-orders.update', {fill_order: props.fillOrder.oid});
  }
  form.placed = datePlaced.value;
  form.expected = dateExpected.value;

  saving.value = true;
  form.post(url, {
    preserveScroll: true,
    onFinish() {
      saving.value = false;
    }
  });
}
const resetForm = (fillOrderData) => {
  if (Object.keys(usePage().props.value.errors).length > 0) {
    return;
  }
  Object.keys(form.data()).forEach(key => {
    if (!['placed', 'expected', '_method'].includes(key)) {
      form[key] = fillOrderData[key];
    }
  });
  datePlaced.value = fillOrderData.placed;
  dateExpected.value = fillOrderData.expected;
}
const parentBreadcrumb = {
  title: 'Incoming Fill Orders',
  href: route('admin.fill-orders.incoming')
};
if (fillOrder?.oid && (fillOrder?.received_at !== null)) {
  parentBreadcrumb.title = 'Received Fill Orders'
  parentBreadcrumb.href = route('admin.fill-orders.received')
}

const formatReceivedAt = () => {
  if (!fillOrder?.oid || (fillOrder?.received_at === null)) {
    return 'Not yet marked as Received';
  }

  return dayjs(fillOrder.received_at).format('MMMM D, YYYY hh:mm A');
}
</script>

<template>
  <AppLayout
    :breadcrumbs="[parentBreadcrumb, {title: pageTitle}]"
    title="Incoming Fill Orders">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-900 leading-tight">
        <span v-text="pageTitle"></span>
      </h2>
    </template>
    <div class="mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div class="border rounded-lg border-gray-200 shadow-lg p-5 grid grid-cols-2 gap-5">
        <div>
          <InputLabel for="vendor" value="Vendor" class="mb-2"/>
          <SelectInput id="vendor" v-model="form.vendor" :required="true" class="w-full">
            <option :value="null">Please select...</option>
            <option v-for="vendor in vendors" :value="vendor.vendorID" v-text="vendor.vendorName"></option>
          </SelectInput>
          <InputError :message="form.errors.vendor" class="mt-2"/>
        </div>
        <div>
          <InputLabel for="company" value="Company" class="mb-2"/>
          <SelectInput v-model="form.client" :required="true" id="company" class="w-full">
            <option :value="null">Please select...</option>
            <option v-for="company in companies" :value="company.ID" v-text="company.client"></option>
          </SelectInput>
          <InputError :message="form.errors.client" class="mt-2"/>
        </div>
        <div>
          <FwbInput label="Project Name" placeholder="Project Name" v-model="form.prj_name"></FwbInput>
          <InputError :message="form.errors.prj_name" class="mt-2"/>
        </div>
        <div>
          <FwbInput label="Purchase Order" placeholder="PO #12345" v-model="form.pu_order"></FwbInput>
          <InputError :message="form.errors.pu_order" class="mt-2"/>
        </div>
        <div>
          <InputLabel for="date-placed" value="Date Placed" class="mb-2"/>
          <Datepicker class="mt-1 block w-full" format="MMMM d, yyyy" id="date-placed" v-model="datePlaced"
                      model-type="MM/dd/yyyy" auto-apply :close-on-auto-apply="true" :enable-time-picker="false"/>
          <InputError :message="form.errors.placed" class="mt-2"/>
        </div>
        <div>
          <InputLabel for="date-expected" value="Date Expected" class="mb-2"/>
          <Datepicker class="mt-1 block w-full" format="MMMM d, yyyy" id="date-expected" v-model="dateExpected"
                      model-type="MM/dd/yyyy" auto-apply :close-on-auto-apply="true" :enable-time-picker="false"/>
          <InputError :message="form.errors.expected" class="mt-2"/>
        </div>
        <div>
          <FwbInput label="Tracking Number" placeholder="11NMO458891STU" v-model="form.trk_num"></FwbInput>
          <InputError :message="form.errors.trk_num" class="mt-2"/>
        </div>
        <div>
          <InputLabel value="Marked as Received at" for="received-at" class="mb-2"/>
          <DisabledInput :value="formatReceivedAt()" id="received-at"></DisabledInput>
        </div>
        <div class="col-span-full" v-if="fillOrder?.oid">
          <MediaUpload
            :media-input="media"
            :can-see-upload-form="true"
            :can-modify-media-details="true"
            :hide-save="false"
            media-location="fill-order"
            :extra-attributes="{fill_order_id: fillOrder.oid}"
          ></MediaUpload>
        </div>
        <div>
          <PrimaryButton class="w-full" @click="saveForm" :disabled="saving">Save</PrimaryButton>
        </div>
        <div>
          <Link :href="route(backRoute)">
            <SecondaryButton class="w-full">Discard</SecondaryButton>
          </Link>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
